import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import BarChartIcon from "@mui/icons-material/BarChart";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import AssignmentLateIcon from "@mui/icons-material/AssignmentLate";
import AddchartIcon from "@mui/icons-material/Addchart";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SendIcon from "@mui/icons-material/Send";
import LowPriorityIcon from "@mui/icons-material/LowPriority";
import { Autocomplete, Box, Chip, Popover, TextField } from "@mui/material";
import { buscarCategoriasTributarias } from "../../services/api-planejamento-tributario.service";
import {
  buscarPreviasInss,
  buscarPreviasInssPorTexto,
  editarPreviasInss,
  iniciarProjetoInss,
} from "../../services/api-inss.service";
import TableCard from "../../components/TableCard";
import { debounce, set } from "lodash";
import { formatValue } from "../../utils/formatValue";
import styles from "./styles.module.css";
import ModalEditar from "./ModalEditar";
import ModalSolicitaDocs from "./ModalSolicitaDocs";
import ModalAnalisar from "./ModalAnalisar";
import { PreviaInssContext } from "./PreviaInssContext";
import SnackBar from "../../components/SnackBar";
import ModalVersoes from "./ModalVersoes";
import ModalNovaPrevia from "./ModalNovaPrevia";
import {
  usePermissoes,
  CHAVES_INSS_CREATE,
  CHAVES_INSS_EDIT,
  CHAVES_INSS_VIEW,
} from "../../config/Context/PermissoesContext";

const PreviaINSS = () => {
  const { pode, carregado, chaves } = usePermissoes();
  const handleOrganizaDadosRef = useRef((raw) => raw);
  const [previas, setPrevias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalEditar, setModalEditar] = useState({ open: false, dados: {} });
  const [categorias, setCategorias] = useState([]);
  const [modalAnalisar, setModalAnalisar] = useState({});
  const { buscaDadosPrevia } = useContext(PreviaInssContext);
  const [objSnackBar, setObjSnackBar] = useState({ type: "", msg: "" });
  const [modalSolicitaDocs, setModalSolicitaDocs] = useState({ open: false, idPrevia: null});
  const [modalVersoes, setModalVersoes] = useState({
    open: false,
    idPrevia: null,
    projeto: false,
  });
  const [modalNovaPrevia, setModalNovaPrevia] = useState({ open: false });
  const [statusFiltro, setStatusFiltro] = useState([]);
  const [statusFilterOpen, setStatusFilterOpen] = useState(false);
  const [statusFilterAnchorEl, setStatusFilterAnchorEl] = useState(null);
  const statusHeaderRef = useRef(null);
  const [categoriaFilterOpen, setCategoriaFilterOpen] = useState(false);
  const [categoriaFilterAnchorEl, setCategoriaFilterAnchorEl] = useState(null);
  const categoriaHeaderRef = useRef(null);
  const [categoriaFiltro, setCategoriaFiltro] = useState([]);

  const columns = [
    "Razão Social",
    "CNPJ",
    "Categoria ",
    "Dt Inicial",
    "Dt Final",
    "Status",
  ];

  // Larguras fixas para as colunas (em pixels)
  const columnWidths = {
    0: "250px", // Razão Social
    1: "180px", // CNPJ
    2: "200px", // Categoria
    3: "120px", // Dt Inicial
    4: "120px", // Dt Final
    5: "180px", // Status
    6: "150px", // Ações
  };

  const handleInititalData = useCallback(async () => {
    setLoading(true);
    try {
      const responsePrevias = await buscarPreviasInss();
      const responseCategorias = await buscarCategoriasTributarias();
      const organizaCategoria = responseCategorias.categories.map(
        (categoria) => {
          return {
            id: categoria.idCategoriaTributaria,
            label: categoria.descricao,
          };
        }
      );
      setCategorias(organizaCategoria);
      setPrevias(handleOrganizaDadosRef.current(responsePrevias.previas));
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!carregado) return;
    handleInititalData();
  }, [carregado, chaves, handleInititalData]);

  // Escuta evento para recarregar dados quando uma prévia for atualizada
  useEffect(() => {
    const handleReload = () => {
      handleInititalData();
    };
    
    window.addEventListener('reload-previas-inss', handleReload);
    
    return () => {
      window.removeEventListener('reload-previas-inss', handleReload);
    };
  }, [handleInititalData]);

  const handleSnackBar = (obj) => {
    setObjSnackBar(obj);
  };

  const handleOrganizaDados = (dados) => {
    const btnAnalisar = (dado) => {
      if (!pode(CHAVES_INSS_VIEW)) return null;
      return {
        icon: <QueryStatsIcon />,
        title: "Analisar",
        onClick: () => handleModalAnalisar(dado),
      };
    };
    const btnIniciarPrevia = (dado) => {
      if (!pode(CHAVES_INSS_EDIT)) return null;
      return {
        icon: <AddchartIcon />,
        title: "INICIAR PRÉVIA",
        onClick: () => handleModalEditar(true, dado),
      };
    };
    const btnDocPendente = (dado) => {
      if (!pode(CHAVES_INSS_EDIT)) return null;
      return {
        icon: <AssignmentLateIcon />,
        title: "SOLICITAR DOCUMENTOS",
        onClick: () => handleModalSolicitaDocs(dado.idPreviaInss),
      };
    };
    const btnGerarProjeto = (dado) => {
      if (!pode(CHAVES_INSS_EDIT)) return null;
      return {
        icon: <SendIcon />,
        title: "GERAR PROJETO",
        onClick: () => handleGerarProjeto(dado),
      };
    };
    const btnNovaVersao = (dado) => {
      if (!pode(CHAVES_INSS_EDIT)) return null;
      return {
        icon: <LowPriorityIcon />,
        title: "NOVA VERSÃO",
        onClick: () => handleModalAnalisar(dado),
      };
    };
    const btnVizualizar = (dado) => {
      if (!pode(CHAVES_INSS_VIEW)) return null;
      return {
        icon: <VisibilityIcon />,
        title: "Visualizar",
        onClick: () => handleModalAnalisar(dado),
      };
    };

    const formatStatus = (status) => {
      switch (status) {
        case "PREVIA-PENDENTE":
          return (
            <span className={styles.status_container}>
              <span className={styles.pendente} /> Pendente
            </span>
          );
        case "PREVIA-ANDAMENTO":
          return (
            <span className={styles.status_container}>
              <span className={styles.andamento} /> Andamento
            </span>
          );
        case "PREVIA-DOCUMENTACAO-PENDENTE":
          return (
            <span className={styles.status_container}>
              <span className={styles.documentacao} /> Documentação Pendente
            </span>
          );
        case "PREVIA-ENVIADA":
          return (
            <span className={styles.status_container}>
              <span className={styles.enviada} /> Enviada
            </span>
          );
        case "PREVIA-APROVADO":
          return (
            <span className={styles.status_container}>
              <span className={styles.aprovada} /> Aprovada
            </span>
          );
        case "PREVIA-CANCELADA":
          return (
            <span className={styles.status_container}>
              <span className={styles.cancelada} /> Cancelada
            </span>
          );
        case "PROJETO-INICIADO":
          return (
            <span className={styles.status_container}>
              <span className={styles.aprovada} /> Projeto Iniciado
            </span>
          );
        default:
          break;
      }
    };

    return dados.map((dado) => {
      const categoriaTexto = dado.categoriaTributaria
        ? (typeof dado.categoriaTributaria === "object" ? dado.categoriaTributaria.descricao : dado.categoriaTributaria)
        : "-";
      // Ordem das chaves deve bater com columns: Razão Social, CNPJ, Categoria, Dt Inicial, Dt Final, Status
      const row = {
        razaoSocial: dado.razaoSocial,
        cnpj: formatValue("cnpj", dado.cnpj),
        categoria: categoriaTexto,
        dtInicial: formatValue("data", dado.dtInicial),
        dtFinal: formatValue("data", dado.dtFinal),
        status: formatStatus(dado.status),
      };
      const formatBtn = (status) => {
        switch (status) {
          case "PREVIA-PENDENTE":
            return [btnIniciarPrevia(dado), btnDocPendente(dado)];
          case "PREVIA-ANDAMENTO":
            return [btnAnalisar(dado)];
          case "PREVIA-APROVADO":
            return [btnGerarProjeto(dado), btnNovaVersao(dado)];
          default:
            return [btnVizualizar(dado)];
        }
      };
      return {
        ...row,
        botoes: formatBtn(dado.status).filter(Boolean),
      };
    });
  };

  handleOrganizaDadosRef.current = handleOrganizaDados;

  const handleModalEditar = (open, dados) => {
    if (!open) {
      setModalEditar({ open, dados: {} });
    } else {
      setModalEditar({
        open: open ?? modalEditar.open,
        dados: dados ?? modalEditar.dados,
      });
    }
  };


const handleGerarProjeto = async (data) => {
  setLoading(true);
  try {
    if (data.status !== "PREVIA-APROVADO") {
      setObjSnackBar({ type: "error", msg: "Prévia precisa estar APROVADA para gerar projeto." });
      setLoading(false);
      return;
    }
    setModalVersoes({ open: true, idPrevia: data.idPreviaInss, projeto: true });
    setModalAnalisar(data);
    } catch (error) {
      console.error(error);
      setObjSnackBar({
        type: "error",
        msg: error.response?.data?.mensagem ?? "Ops, erro ao gerar projeto.",
      });
    } finally {setLoading(false)}
  };

  const handleModalAnalisar = async (data) => {
    // setOpenOrCloseModalAnalisar(true);
    sessionStorage.setItem(
      "cnpjSelecionado",
      String(data?.cnpj || "").replace(/\D/g, "")
    );
    setModalVersoes({ open: true, idPrevia: data.idPreviaInss });
    setModalAnalisar(data);
  };
  
  const handleModalSolicitaDocs = (idPrevia) => {
    setModalSolicitaDocs({ open: true, idPrevia });
  };

  const handlePesquisaPorTexto = async (texto) => {
    setLoading(true);
    try {
      const responsePrevias = await buscarPreviasInssPorTexto(texto);
      setPrevias(handleOrganizaDadosRef.current(responsePrevias.previas));
    } catch (error) {
      console.error(error);
      handleSnackBar({
        type: "error",
        msg: error.response?.data?.mensagem ?? "Ops, erro ao buscar prévias.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDebounce = debounce(handlePesquisaPorTexto, 300);

  // Opções de filtro de Status com cores
  const statusOptions = [
    { value: "Pendente", label: "Pendente", color: "red" },
    { value: "Andamento", label: "Andamento", color: "orange" },
    { value: "Documentação Pendente", label: "Documentação Pendente", color: "rgb(255, 102, 0)" },
    { value: "Enviada", label: "Enviada", color: "rgb(28, 42, 235)" },
    { value: "Aprovada", label: "Aprovada", color: "green" },
    { value: "Cancelada", label: "Cancelada", color: "rgb(177, 0, 0)" },
    { value: "Projeto Iniciado", label: "Projeto Iniciado", color: "green" },
  ];

  // Função auxiliar para extrair texto de elemento React
  const extractTextFromElement = (element) => {
    if (!element) return "";
    if (typeof element === "string") return element;
    if (!React.isValidElement(element)) return String(element);
    
    const children = element.props?.children;
    if (!children) return "";
    
    if (Array.isArray(children)) {
      return children
        .map((child) => {
          if (typeof child === "string") return child;
          if (React.isValidElement(child)) {
            return extractTextFromElement(child);
          }
          return String(child || "");
        })
        .join(" ")
        .trim();
    }
    
    if (typeof children === "string") return children;
    if (React.isValidElement(children)) return extractTextFromElement(children);
    
    return String(children || "");
  };

  // Filtrar dados por status e categoria selecionados
  const previasFiltradas = previas.filter((previa) => {
    // Filtro por Status
    if (statusFiltro.length > 0) {
      const statusText = extractTextFromElement(previa.status);
      const matchesStatus = statusFiltro.some((filtro) => {
        const filtroLower = filtro.toLowerCase();
        return statusText.toLowerCase().includes(filtroLower);
      });
      if (!matchesStatus) return false;
    }
    
    // Filtro por Categoria
    if (categoriaFiltro.length > 0) {
      const categoriaText = String(previa.categoria || "").trim();
      const matchesCategoria = categoriaFiltro.some((filtro) => {
        const filtroLower = filtro.toLowerCase();
        return categoriaText.toLowerCase().includes(filtroLower);
      });
      if (!matchesCategoria) return false;
    }
    
    return true;
  });

  // Não fechar automaticamente quando não há resultados - permitir que usuário remova os filtros manualmente

  return (
    <>
      <div className={styles.containerPreviaInss}>
        <TableCard
          iconHeader={<BarChartIcon fontSize="1rem" />}
          titleHeader="Prévias INSS"
          data={previasFiltradas}
          setData={setPrevias}
          columns={columns}
          textHeaderCard="razaoSocial"
          handleDebounce={handleDebounce}
          loading={loading}
          temInsert={pode(CHAVES_INSS_CREATE)}
          insertOpenModal={() => setModalNovaPrevia({ open: true })}
          insertFabVariant="vinculos"
          columnWidths={columnWidths}
          onStatusHeaderClick={() => {
            // Atualizar anchorEl antes de abrir para garantir posicionamento correto
            if (statusHeaderRef.current) {
              setStatusFilterAnchorEl(statusHeaderRef.current);
              setStatusFilterOpen(true);
            }
          }}
          statusHeaderRef={statusHeaderRef}
          onCategoriaHeaderClick={() => {
            // Atualizar anchorEl antes de abrir para garantir posicionamento correto
            if (categoriaHeaderRef.current) {
              setCategoriaFilterAnchorEl(categoriaHeaderRef.current);
              setCategoriaFilterOpen(true);
            }
          }}
          categoriaHeaderRef={categoriaHeaderRef}
          tableClassName={styles.tablePreviaInss}
        />
        <Popover
          open={statusFilterOpen && !!statusFilterAnchorEl}
          anchorEl={statusFilterAnchorEl}
          onClose={() => {
            setStatusFilterOpen(false);
            setStatusFilterAnchorEl(null);
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{ paper: { sx: { mt: 1.5, minWidth: 280 } } }}
          disableAutoFocus
          disableEnforceFocus
        >
          <Box sx={{ p: 2 }}>
            <Autocomplete
              multiple
              options={statusOptions}
              value={statusOptions.filter((opt) => statusFiltro.includes(opt.value))}
              onChange={(_, newValue) => {
                const newFiltro = newValue.map((v) => v.value);
                setStatusFiltro(newFiltro);
                // Se removeu todos os filtros, fechar o popover imediatamente
                if (newFiltro.length === 0) {
                  setStatusFilterOpen(false);
                  setStatusFilterAnchorEl(null);
                }
              }}
              getOptionLabel={(option) => option.label}
              renderOption={(props, option) => (
                <Box component="li" {...props} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <span
                    style={{
                      width: "12px",
                      height: "12px",
                      borderRadius: "50%",
                      backgroundColor: option.color,
                      display: "inline-block",
                    }}
                  />
                  {option.label}
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  variant="outlined"
                  label="Filtrar por Status"
                  autoFocus
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#18316e" },
                      "&:hover fieldset": { borderColor: "#18316e" },
                      "&.Mui-focused fieldset": { borderColor: "#18316e" },
                    },
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.value}
                    label={option.label}
                    size="small"
                    sx={{
                      "& .MuiChip-deleteIcon": { color: option.color },
                    }}
                  />
                ))
              }
            />
          </Box>
        </Popover>
        <Popover
          open={categoriaFilterOpen && !!categoriaFilterAnchorEl}
          anchorEl={categoriaFilterAnchorEl}
          onClose={() => {
            setCategoriaFilterOpen(false);
            setCategoriaFilterAnchorEl(null);
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{ paper: { sx: { mt: 1.5, minWidth: 280 } } }}
          disableAutoFocus
          disableEnforceFocus
        >
          <Box sx={{ p: 2 }}>
            <Autocomplete
              multiple
              options={categorias}
              value={categorias.filter((cat) => categoriaFiltro.includes(cat.label))}
              onChange={(_, newValue) => {
                const newFiltro = newValue.map((v) => v.label);
                setCategoriaFiltro(newFiltro);
                // Se removeu todos os filtros, fechar o popover imediatamente
                if (newFiltro.length === 0) {
                  setCategoriaFilterOpen(false);
                  setCategoriaFilterAnchorEl(null);
                }
              }}
              getOptionLabel={(option) => option.label}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  variant="outlined"
                  label="Filtrar por Categoria"
                  autoFocus
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#18316e" },
                      "&:hover fieldset": { borderColor: "#18316e" },
                      "&.Mui-focused fieldset": { borderColor: "#18316e" },
                    },
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={option.label}
                    size="small"
                  />
                ))
              }
            />
          </Box>
        </Popover>
      </div>

      <ModalEditar
        open={modalEditar.open}
        onClose={() => handleModalEditar(false)}
        dados={modalEditar.dados}
        categorias={categorias}
        rechargeData={handleInititalData}
        newPrevia
      />

      <ModalSolicitaDocs
        open={modalSolicitaDocs.open}
        setOpen={(value) =>
          setModalSolicitaDocs({ ...modalSolicitaDocs, open: value })
        }
        idPrevia={modalSolicitaDocs.idPrevia}
        product={"INSS"}
      />

      <ModalVersoes
        open={modalVersoes.open}
        onClose={() => setModalVersoes({ open: false, idPrevia: null, projeto: false })}
        id={modalVersoes.idPrevia}
        tipo="INSS"
        data={modalAnalisar}
        rechargeData={handleInititalData}
        gerarProjeto={modalVersoes.projeto}
      />

      <ModalNovaPrevia
        open={modalNovaPrevia.open}
        onClose={() => setModalNovaPrevia({ open: false })}
        categorias={categorias}
        rechargeData={handleInititalData}
      />

      <SnackBar objSnackBar={objSnackBar} setObjSnackBar={handleSnackBar} />
    </>
  );
};

export default PreviaINSS;
